import { useContext, useMemo } from 'react';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import { DeleteModal } from '@/modules/admin-dashboard/components/modals/DeleteModal.tsx';
import { FormTagModal } from '@/modules/admin-dashboard/components/modals/FormTagModal.tsx';
import { BetaBadge } from '@/modules/admin-dashboard/pages/AIChat.tsx';
import {
  useDeleteFormTagMutation,
  useGetFormTagsListQuery,
} from '@/modules/admin-dashboard/queries/form-tags.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { ModalProviderContext, SidebarMenuItem } from 'ui';
import {
  AIChatGrayIcon,
  AIChatWhiteIcon,
  AlertGrayIcon,
  AlertWhiteIcon,
  ClientsIcon,
  ClientsWhiteIcon,
  DataFlowGrayIcon,
  DepartmentWhiteIcon,
  DepartmentsIcon,
  EditLightGrayIcon,
  FileGreyIcon,
  FileWhiteIcon,
  FilesDisabledIcon,
  FilesIcon,
  FilesWhiteIcon,
  FormGrayIcon,
  FormWhiteIcon,
  HomeGrayIcon,
  HomeWhiteIcon,
  LockLightGrayIcon,
  LockWhiteIcon,
  MeetingGrayIcon,
  MeetingWhiteIcon,
  NotificationsDisabledIcon,
  NotificationsGrayIcon,
  NotificationsWhiteIcon,
  PlusGrayIcon,
  ScalesIcon,
  ScalesWhiteIcon,
  SettingsGrayIcon,
  SettingsWhiteIcon,
  TaskIcon,
  TasksInactive,
  TrashGrayNonTransparentIcon,
} from 'ui/icons';
import { TaskListMode } from 'compliance-api';

export const useSidebarOptions = (): SidebarMenuItem[] => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);

  const { data: tagsData } = useGetFormTagsListQuery();

  const formsSubItems = useMemo<SidebarMenuItem[] | undefined>(
    () => [
      ...(tagsData?.tags || []).map(tag => ({
        label: tag.name,
        labelAction: (
          <div
            className="w-0 group-hover:w-auto flex gap-3 shrink-0"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={() => openModal(FormTagModal, { tag })}>
              <img alt="edit" src={EditLightGrayIcon} className="w-5 transition-all" />
            </button>
            <button
              onClick={() => {
                openModal(DeleteModal, {
                  id: tag.id,
                  title: 'tag',
                  mutation: useDeleteFormTagMutation,
                  name: tag.name,
                });
              }}
            >
              <img
                alt="delete"
                src={TrashGrayNonTransparentIcon}
                className='className="w-5 transition-all'
              />
            </button>
          </div>
        ),
        labelClassName: 'flex w-full justify-between group h-6',
        pathname: adminDashboardPaths.dynamic.formsByTag(tag.id),
        onClick: () => {
          navigate({
            pathname: adminDashboardPaths.dynamic.formsByTag(tag.id),
            search: params.toString(),
          });
        },
      })),
      {
        label: 'New category',
        icon: PlusGrayIcon,
        pathname: '',
        searchKeywords: ['create', 'add'],
        onClick: () => openModal(FormTagModal),
      },
    ],
    [tagsData?.tags, openModal, navigate, params],
  );

  const search = createSearchParams({
    [BUSINESS_QUERY_PARAM]: params.get(BUSINESS_QUERY_PARAM) || '',
  }).toString();

  return useMemo(
    () => [
      {
        label: 'Dashboard',
        activeIcon: HomeWhiteIcon,
        icon: HomeGrayIcon,
        pathname: adminDashboardPaths.static.dashboard,
        searchKeywords: ['overview', 'summary', 'stats'],
        tooltipHelpText: 'View a summary of key compliance metrics and recent activity.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.dashboard,
            search,
          }),
      },
      {
        label: 'Events',
        icon: AlertGrayIcon,
        activeIcon: AlertWhiteIcon,
        disabledIcon: AlertGrayIcon,
        pathname: adminDashboardPaths.static.incidents,
        searchKeywords: ['calendar', 'schedule', 'alerts', 'incidents'],
        tooltipHelpText:
          'Report important events as they happen, like incidents, breaches or employee trainings.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.incidents,
            search,
          }),
      },
      {
        label: 'Compliance Progress',
        activeIcon: ScalesWhiteIcon,
        icon: ScalesIcon,
        pathname: adminDashboardPaths.static.registers,
        searchKeywords: ['status', 'audit', 'regulation', 'registers'],
        tooltipHelpText: 'Track compliance status based on tasks users have completed.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.registers,
            search,
          }),
      },
      {
        label: 'Clients',
        activeIcon: ClientsWhiteIcon,
        icon: ClientsIcon,
        pathname: adminDashboardPaths.static.clients,
        searchKeywords: ['clients'],
        tooltipHelpText:
          'Manage your organisation’s clients,   track attestations and related documents and contracts.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.clients,
            search,
          }),
      },
      {
        label: 'Forms',
        activeIcon: FormWhiteIcon,
        icon: FormGrayIcon,
        pathname: adminDashboardPaths.static.forms,
        searchKeywords: ['templates', 'input', 'submission'],
        tooltipHelpText:
          'Add fields with questions which your employees will need to answer when completing Tasks or reporting Events.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.forms,
            search,
          }),
        subItems: formsSubItems,
      },
      {
        label: 'Tasks',
        activeIcon: TaskIcon,
        icon: TasksInactive,
        pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.AllTasks),
        searchKeywords: ['to-do', 'upcoming', 'overdue', 'todo'],
        tooltipHelpText:
          'This allocates tasks to certain people within the organisation to meet deadlines and compliance.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.AllTasks),
            search,
          }),
        subItems: [
          {
            label: 'To Do',
            pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.Todo),
            searchKeywords: ['pending', 'action', 'todo'],
            tooltipHelpText: 'View current tasks requiring your attention.',
            onClick: () => {
              navigate({
                pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.Todo),
                search: params.toString(),
              });
            },
          },
          {
            label: 'Upcoming',
            pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.Upcoming),
            searchKeywords: ['schedule', 'planned', 'future'],
            tooltipHelpText: 'See tasks scheduled for the near future.',
            onClick: () => {
              navigate({
                pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.Upcoming),
                search: params.toString(),
              });
            },
          },
          {
            label: 'Overdue',
            pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.Overdue),
            searchKeywords: ['late', 'past', 'delay'],
            tooltipHelpText:
              'Review tasks that are past their due date. These tasks still can be completed.',
            onClick: () => {
              navigate({
                pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.Overdue),
                search: params.toString(),
              });
            },
          },
          {
            label: 'History',
            pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.History),
            searchKeywords: ['log', 'past', 'archive'],
            tooltipHelpText: 'A log of completed tasks.',
            onClick: () => {
              navigate({
                pathname: adminDashboardPaths.dynamic.taskCategory(TaskListMode.History),
                search: params.toString(),
              });
            },
          },
        ],
      },
      {
        label: 'Reports',
        icon: FileGreyIcon,
        activeIcon: FileWhiteIcon,
        disabledIcon: FileGreyIcon,
        pathname: '',
        opener: true,
        searchKeywords: ['on-demand', 'templates', 'batch', 'export'],
        tooltipHelpText: 'Generate, view, and manage different types of compliance reports.',
        subItems: [
          {
            label: 'On demand',
            pathname: adminDashboardPaths.static.onDemandReports,
            searchKeywords: ['instant', 'generate'],
            tooltipHelpText: 'Generate reports instantly based on selected parameters.',
            onClick: () =>
              navigate({
                pathname: adminDashboardPaths.static.onDemandReports,
                search,
              }),
          },
          {
            label: 'Template reports',
            pathname: adminDashboardPaths.static.templateReports,
            searchKeywords: ['prebuilt', 'standard', 'format'],
            tooltipHelpText: 'Use pre-built report templates for consistent output.',
            onClick: () =>
              navigate({
                pathname: adminDashboardPaths.static.templateReports,
                search,
              }),
          },
          {
            label: 'Batch reports',
            pathname: adminDashboardPaths.static.batchReports,
            searchKeywords: ['bulk', 'multiple', 'process'],
            tooltipHelpText:
              'Create multiple reports simultaneously when you need a mix of different reports all at once into 1 PDF.',
            onClick: () =>
              navigate({
                pathname: adminDashboardPaths.static.batchReports,
                search,
              }),
          },
          {
            label: 'Fixed reports',
            pathname: adminDashboardPaths.static.fixedReports,
            searchKeywords: ['static', 'predefined', 'locked'],
            tooltipHelpText:
              'This produces a report from inception date of an entry to the current date using templates.',
            onClick: () =>
              navigate({
                pathname: adminDashboardPaths.static.fixedReports,
                search,
              }),
          },
        ],
      },
      {
        label: 'Departments',
        activeIcon: DepartmentWhiteIcon,
        icon: DepartmentsIcon,
        pathname: adminDashboardPaths.static.departments,
        searchKeywords: ['teams', 'units', 'divisions', 'crew', 'groups'],
        tooltipHelpText:
          'This is to allocate certain tasks to certain departments within the organisation (i.e., HR, legal, finance).',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.departments,
            search,
          }),
      },
      {
        label: 'Documents',
        icon: FilesIcon,
        activeIcon: FilesWhiteIcon,
        disabledIcon: FilesDisabledIcon,
        searchKeywords: ['files', 'records', 'archives'],
        tooltipHelpText: 'Store and access official compliance documents and records.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.documents,
            search,
          }),
        pathname: adminDashboardPaths.static.documents,
      },
      {
        label: 'Settings',
        activeIcon: SettingsWhiteIcon,
        icon: SettingsGrayIcon,
        pathname: '',
        opener: true,
        searchKeywords: ['preferences', 'configuration', 'options'],
        tooltipHelpText: 'Customize system settings and manage users and organisational policies.',
        subItems: [
          {
            label: 'User Management',
            pathname: adminDashboardPaths.static.management,
            searchKeywords: ['users', 'profiles', 'permissions'],
            tooltipHelpText: 'Manage user accounts and access levels, add new users.',
            onClick: () =>
              navigate({
                pathname: adminDashboardPaths.static.management,
                search,
              }),
          },
          {
            label: 'Organisation Settings',
            pathname: adminDashboardPaths.static.organizationsSettings,
            searchKeywords: ['config', 'policies', 'structure'],
            tooltipHelpText: 'Allows to edit name and branding of your organisation.',
            onClick: () =>
              navigate({
                pathname: adminDashboardPaths.static.organizationsSettings,
                search,
              }),
          },
        ],
      },
      {
        label: (
          <span className="flex items-center gap-3">
            AI Chat <BetaBadge />
          </span>
        ),
        icon: AIChatGrayIcon,
        activeIcon: AIChatWhiteIcon,
        disabledIcon: AIChatGrayIcon,
        pathname: adminDashboardPaths.static.aiChat,
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.aiChat,
            search,
          }),
      },
      {
        label: 'Meetings',
        icon: MeetingGrayIcon,
        activeIcon: MeetingWhiteIcon,
        disabledIcon: MeetingGrayIcon,
        searchKeywords: ['schedule', 'video', 'conference', 'meeting', 'call'],
        tooltipHelpText:
          'You can use this feature to create Microsoft Teams meetings and Calendar records for online and offline meetings.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.meetings,
            search,
          }),
        pathname: adminDashboardPaths.static.meetings,
      },
      {
        label: 'Vaults',
        icon: LockLightGrayIcon,
        activeIcon: LockWhiteIcon,
        disabledIcon: LockLightGrayIcon,
        pathname: adminDashboardPaths.static.secretManager,
        searchKeywords: ['vault', 'secrets', 'keys', 'credentials', 'secret manager'],
        tooltipHelpText:
          'Store sensitive values in vaults and control access at vault level for your team.',
        onClick: () =>
          navigate({
            pathname: adminDashboardPaths.static.secretManager,
            search,
          }),
      },
      {
        label: 'Audit log',
        icon: NotificationsGrayIcon,
        activeIcon: NotificationsWhiteIcon,
        disabledIcon: NotificationsDisabledIcon,
        pathname: adminDashboardPaths.static.auditLog,
        onClick: () =>
          navigate(
            {
              pathname: adminDashboardPaths.static.auditLog,
              search,
            },
            {
              replace: false,
            },
          ),
      },
      {
        label: 'Diagrams',
        icon: DataFlowGrayIcon,
        activeIcon: DataFlowGrayIcon,
        disabledIcon: DataFlowGrayIcon,
        disabled: true,
        searchKeywords: ['charts', 'visuals', 'flowcharts'],
        tooltipHelpText:
          'Coming soon: Build process diagrams, organisational charts and entity relationship diagrams that clearly illustrate your internal workflows.',
        onClick: () => navigate({ pathname: '', search }),
        pathname: '',
      },
    ],
    [formsSubItems, navigate, params, search],
  );
};
